import { useRef, useEffect, useState, useCallback } from "react";
import { Spreadsheet, Worksheet, jspreadsheet } from "@jspreadsheet/react";
import "jsuites/dist/jsuites.css";
import "jspreadsheet/dist/jspreadsheet.css";
import "./PingSovScrubber.scss";

const urlParams = new URLSearchParams(window.location.search);
const USE_DEBUG_LOGGING = urlParams.get("debug") === "true";
const NO_STYLES = urlParams.get("nostyles") === "true";

/** Convert a 0-based column index to an Excel-style letter (0→A, 25→Z, 26→AA). */
const colIndexToLetter = (index: number): string => {
  let result = "";
  let n = index;
  while (n >= 0) {
    result = String.fromCharCode((n % 26) + 65) + result;
    n = Math.floor(n / 26) - 1;
  }
  return result;
};

/** Format a number for display — use locale string for readability. */
const formatStatNumber = (value: number): string => {
  if (Number.isInteger(value)) return value.toLocaleString();
  return value.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
};

interface SelectionStats {
  count: number;
  numericCount: number;
  sum: number;
}

/** Convert a cell name like "A1" to [colIndex, rowIndex] (0-based). */
const cellNameToCoords = (name: string): [number, number] => {
  const match = name.match(/^([A-Z]+)(\d+)$/);
  if (!match) return [-1, -1];
  let col = 0;
  for (let i = 0; i < match[1].length; i++) {
    col = col * 26 + (match[1].charCodeAt(i) - 64);
  }
  return [col - 1, parseInt(match[2], 10) - 1];
};

/** CSS properties that can hide content — stripped from cell styles. */
const PROBLEMATIC_CSS_PROPS = new Set([
  "display", "visibility", "opacity", "height", "max-height", "overflow",
]);

/** Sanitize a CSS string by removing problematic properties. */
const sanitizeCss = (css: string): string =>
  css
    .split(";")
    .filter((rule) => {
      const prop = rule.split(":")[0]?.trim().toLowerCase();
      return prop && !PROBLEMATIC_CSS_PROPS.has(prop);
    })
    .join(";");

/**
 * Build a <style> textContent for a single worksheet's cell styles.
 * Groups cells by style index so each unique CSS string is a single rule
 * with multiple attribute selectors.
 */
const buildWorksheetStyleCSS = (
  deferredStyle: Record<string, number>,
  styleDefs: string[],
): string => {
  // Group cells by style index
  const groups = new Map<number, string[]>();
  for (const [cell, idx] of Object.entries(deferredStyle)) {
    const numIdx = typeof idx === "number" ? idx : parseInt(idx, 10);
    if (!groups.has(numIdx)) groups.set(numIdx, []);
    groups.get(numIdx)!.push(cell);
  }

  const rules: string[] = [];
  for (const [idx, cells] of groups) {
    const css = sanitizeCss(styleDefs[idx] || "");
    if (!css) continue;
    const selectors = cells.map((cell) => {
      const [x, y] = cellNameToCoords(cell);
      return `td[data-x="${x}"][data-y="${y}"]`;
    });
    // Split into chunks of 200 selectors to avoid browser limits
    for (let i = 0; i < selectors.length; i += 200) {
      rules.push(`${selectors.slice(i, i + 200).join(",")} { ${css} }`);
    }
  }
  return rules.join("\n");
};

const ZOOM_MIN = 0.2;
const ZOOM_MAX = 2;
const ZOOM_STEP = 0.1;
const ZOOM_PRESETS = [20, 40, 60, 80, 100, 120, 140, 160, 180, 200];

interface PingSovScrubberProps {
  onLoad?: (instance: any) => void;
  onError?: (error: any) => void;
  locale?: string;
  enableFilters?: boolean;
  enableColumnSorting?: boolean;
  authToken: string;
  outputFileName: string;
  isReadOnly?: boolean;
  url: string;
}

const PingSovScrubber = ({
  onLoad,
  onError,
  locale = "en-GB",
  enableFilters = true,
  enableColumnSorting = true,
  authToken,
  outputFileName,
  isReadOnly = true,
  url,
}: PingSovScrubberProps) => {
  const spreadsheetRef = useRef(null);
  const containerRef = useRef<HTMLDivElement>(null);
  // Track the jss DOM element separately for cleanup, since containerRef
  // may be nulled by React before our unmount effect runs.
  const jssElementRef = useRef<HTMLElement | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [worksheetData, setWorksheetData] = useState<any>(null);
  const [isReady, setIsReady] = useState(false);
  const loadingRef = useRef(false);
  // <style> element used for deferred per-worksheet cell styles.
  // Only the active worksheet's styles are injected at a time.
  const deferredStyleElRef = useRef<HTMLStyleElement | null>(null);
  // Capture the initial authToken for the fetch — we don't want to
  // re-fetch when the token refreshes since the data is already loaded.
  const authTokenRef = useRef(authToken);
  if (!authTokenRef.current) {
    authTokenRef.current = authToken;
  }

  // Formula bar state
  const [formulaBarCell, setFormulaBarCell] = useState("");
  const [formulaBarValue, setFormulaBarValue] = useState("");

  // Selection statistics state
  const [selectionStats, setSelectionStats] = useState<SelectionStats | null>(
    null,
  );

  // Zoom state
  const [zoomLevel, setZoomLevel] = useState(1);
  const [zoomMenuOpen, setZoomMenuOpen] = useState(false);

  // Store the last active worksheet instance from onselection so zoom
  // buttons can call setZoom without guessing the ref structure.
  const activeWorksheetRef = useRef<any>(null);

  // Store the last selection coordinates so we can re-apply them after zoom.
  const lastSelectionRef = useRef<{
    px: number;
    py: number;
    ux: number;
    uy: number;
  } | null>(null);

  /** Swap the deferred <style> element when the user changes tabs. */
  const handleOpenWorksheet = useCallback(
    (_spreadsheet: any, wsIdx: number) => {
      if (NO_STYLES || !worksheetData?.worksheets) return;
      const wsConfig = worksheetData.worksheets[wsIdx];
      const deferred = wsConfig?._deferredStyle;
      const styleDefs: string[] = worksheetData._styleDefs || [];

      if (!deferredStyleElRef.current) {
        deferredStyleElRef.current = document.createElement("style");
        deferredStyleElRef.current.setAttribute("data-jss-deferred", "true");
        document.head.appendChild(deferredStyleElRef.current);
      }

      if (deferred) {
        deferredStyleElRef.current.textContent = buildWorksheetStyleCSS(
          deferred,
          styleDefs,
        );
      } else {
        // No styles for this worksheet — clear
        deferredStyleElRef.current.textContent = "";
      }
    },
    [worksheetData],
  );

  /** Handle selection changes — update formula bar and selection stats. */
  const handleSelection = useCallback(
    (worksheet: any, px: number, py: number, ux: number, uy: number) => {
      // Keep a live reference for zoom controls
      activeWorksheetRef.current = worksheet;

      // Store selection range so zoom can re-apply it
      lastSelectionRef.current = { px, py, ux, uy };

      // Formula bar: show the anchor cell (where the user actually clicked).
      // onselection normalises coords to top-left / bottom-right, so use
      // getSelection(true) which preserves the original click order.
      let anchorX = px;
      let anchorY = py;
      try {
        const sel = worksheet.getSelection?.(true);
        if (Array.isArray(sel) && sel.length >= 2) {
          anchorX = sel[0];
          anchorY = sel[1];
        }
      } catch {
        // fall back to px, py
      }

      const cellRef = `${colIndexToLetter(anchorX)}${anchorY + 1}`;
      setFormulaBarCell(cellRef);
      try {
        const value = worksheet.getValueFromCoords(anchorX, anchorY);
        setFormulaBarValue(value != null ? String(value) : "");
      } catch {
        setFormulaBarValue("");
      }

      // Selection stats: iterate range and accumulate
      const minX = Math.min(px, ux);
      const maxX = Math.max(px, ux);
      const minY = Math.min(py, uy);
      const maxY = Math.max(py, uy);

      let count = 0;
      let numericCount = 0;
      let sum = 0;

      for (let y = minY; y <= maxY; y++) {
        for (let x = minX; x <= maxX; x++) {
          try {
            const val = worksheet.getValueFromCoords(x, y);
            if (val != null && val !== "") {
              count++;
              const cleaned =
                typeof val === "string"
                  ? val.replace(/[$,%\s]/g, "")
                  : String(val);
              const num = Number(cleaned);
              if (!isNaN(num) && cleaned !== "") {
                numericCount++;
                sum += num;
              }
            }
          } catch {
            // skip inaccessible cells
          }
        }
      }

      setSelectionStats(count > 0 ? { count, numericCount, sum } : null);
    },
    [],
  );

  /** Apply zoom via CSS transform: scale() on the scaler div.
   *  After scaling, force jspreadsheet's content element to match the scaler's
   *  full dimensions so it renders rows for the entire visible area. */
  const applyZoom = useCallback((newZoom: number) => {
    const clamped = Math.round(Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, newZoom)) * 10) / 10;
    setZoomLevel(clamped);

    // After React re-renders the scaler div with new dimensions,
    // force jspreadsheet to recalculate its layout for the new size.
    requestAnimationFrame(() => {
      if (!containerRef.current) return;
      // Set jss_content height to fill the scaler div (which is 1/zoom of wrapper)
      const jssContent = containerRef.current.querySelector(
        ".jss_content",
      ) as HTMLElement;
      if (jssContent) {
        jssContent.style.height = "100%";
      }
      window.dispatchEvent(new Event("resize"));

      // Re-apply the selection so the overlay redraws for the new viewport.
      // jspreadsheet's .jss_border overlay has fixed pixel dimensions from
      // when the selection was made — zooming changes the visible area but
      // doesn't trigger a selection redraw.
      const ws = activeWorksheetRef.current;
      const sel = lastSelectionRef.current;
      if (ws && sel && typeof ws.updateSelectionFromCoords === "function") {
        requestAnimationFrame(() => {
          ws.updateSelectionFromCoords(sel.px, sel.py, sel.ux, sel.uy);
        });
      }
    });
  }, []);

  // Compensate gridline borders so they survive transform: scale().
  // At sub-100% zoom, 1px borders become sub-pixel and anti-alias to a
  // faint grey. Thicken them by 1/zoom and darken the border color.
  useEffect(() => {
    const style = document.createElement("style");
    style.setAttribute("data-jss-zoom-fix", "true");
    if (zoomLevel >= 1) {
      style.textContent = "";
    } else {
      // Use ceil(1/zoom) so borders are always at least 1 full CSS pixel,
      // guaranteeing visibility after scale() downsampling.
      const bw = Math.ceil(1 / zoomLevel);
      style.textContent = `
        .jss-spreadsheet-scaler .jss_container {
          --jss-border-color: #888 !important;
        }
        .jss-spreadsheet-scaler td,
        .jss-spreadsheet-scaler th {
          border-top-width: ${bw}px !important;
          border-left-width: ${bw}px !important;
        }
        .jss-spreadsheet-scaler .jss_viewport {
          box-shadow: inset ${bw}px 0 0 0 var(--jss-border-color) !important;
        }
      `;
    }
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, [zoomLevel]);

  // Close zoom menu when clicking anywhere else
  useEffect(() => {
    if (!zoomMenuOpen) return;
    const close = () => setZoomMenuOpen(false);
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [zoomMenuOpen]);

  if (USE_DEBUG_LOGGING) {
    console.log(
      "[SCRUBBER] Component render - isLoading:",
      isLoading,
      "hasData:",
      !!worksheetData,
    );
  }

  // Destroy the jspreadsheet instance on unmount. The @jspreadsheet/react
  // wrapper never calls destroy(), leaking event listeners and <style> elements.
  useEffect(() => {
    return () => {
      if (jssElementRef.current) {
        try {
          jspreadsheet.destroy(jssElementRef.current);
        } catch {
          // Ignore cleanup errors
        }
        jssElementRef.current = null;
      }
      // Clean up deferred style element
      if (deferredStyleElRef.current) {
        deferredStyleElRef.current.remove();
        deferredStyleElRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsReady(true);
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (worksheetData && USE_DEBUG_LOGGING) {
      console.log("[SCRUBBER] worksheetData effect triggered");
    }

    // Capture the jss DOM element for cleanup. We track it in a ref
    // because containerRef may be nulled by React before unmount effects run.
    if (containerRef.current) {
      jssElementRef.current = containerRef.current.querySelector(
        ".jss_container",
      ) as HTMLElement;
    }

    if (spreadsheetRef.current) {
      const instance = (spreadsheetRef.current as any).current;
      if (instance && typeof instance.refresh === "function") {
        instance.refresh();
      }

      // Inject cell styles for the active worksheet via a <style> element.
      // Only the visible tab's styles are loaded at a time — swapped on
      // tab change — so we never process all 19k+ mappings at once.
      if (!NO_STYLES && worksheetData?.worksheets) {
        const jssContainer = containerRef.current?.querySelector(
          ".jss_container",
        ) as any;
        const parent = jssContainer?.spreadsheet;
        if (parent) {
          const styleDefs: string[] =
            worksheetData._styleDefs || [];
          const activeIdx = parent.getWorksheetActive?.() ?? 0;
          const wsConfig = worksheetData.worksheets[activeIdx];
          const deferred = wsConfig?._deferredStyle;
          if (deferred) {
            if (!deferredStyleElRef.current) {
              deferredStyleElRef.current = document.createElement("style");
              deferredStyleElRef.current.setAttribute(
                "data-jss-deferred",
                "true",
              );
              document.head.appendChild(deferredStyleElRef.current);
            }
            deferredStyleElRef.current.textContent = buildWorksheetStyleCSS(
              deferred,
              styleDefs,
            );
          }
        }
      }
    }
  }, [worksheetData]);

  // Trigger jspreadsheet recalculation on container resize.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    if (!worksheetData) return;

    const observer = new ResizeObserver(() => {
      window.dispatchEvent(new Event("resize"));
    });

    observer.observe(el);
    return () => observer.disconnect();
  }, [worksheetData]);

  // Refresh jspreadsheet when the browser tab becomes visible or regains focus.
  // Browsers may freeze or discard rendering state for background tabs,
  // causing jspreadsheet to appear blank when the user returns.
  useEffect(() => {
    if (!worksheetData) return;

    const refreshSpreadsheet = () => {
      if (USE_DEBUG_LOGGING) {
        console.log("[SCRUBBER] Refreshing spreadsheet (visibility/focus)");
      }
      const instance = (spreadsheetRef.current as any)?.current;
      if (instance && typeof instance.refresh === "function") {
        instance.refresh();
      }
      window.dispatchEvent(new Event("resize"));
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        refreshSpreadsheet();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("focus", refreshSpreadsheet);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", refreshSpreadsheet);
    };
  }, [worksheetData]);

  useEffect(() => {
    if (USE_DEBUG_LOGGING) {
      console.log(
        "[SCRUBBER] Load effect triggered - url:",
        url,
        "loading:",
        loadingRef.current,
      );
    }

    if (!url || !authTokenRef.current || loadingRef.current) {
      return;
    }

    const abortController = new AbortController();

    const loadSpreadsheet = async () => {
      try {
        if (!jspreadsheet || !jspreadsheet.parser) {
          console.log("[SCRUBBER] jspreadsheet is not initialized");
          return;
        }

        if (USE_DEBUG_LOGGING) {
          console.log("[SCRUBBER] Starting loadSpreadsheet");
        }

        // Destroy old jspreadsheet instance before re-loading to prevent
        // the @jspreadsheet/react init guard (!mainReference.current) from
        // seeing a stale ref and skipping initialization on remount.
        if (jssElementRef.current) {
          try {
            jspreadsheet.destroy(jssElementRef.current);
          } catch {
            // Ignore cleanup errors
          }
          jssElementRef.current = null;
        }
        spreadsheetRef.current = null;

        loadingRef.current = true;
        setIsLoading(true);
        const headers: HeadersInit = {
          Authorization: `Bearer ${authTokenRef.current}`,
        };

        if (USE_DEBUG_LOGGING) {
          console.log("[SCRUBBER] Fetching from URL:", url);
        }
        const response = await fetch(url, {
          headers,
          signal: abortController.signal,
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch file: ${response.statusText}`);
        }

        if (USE_DEBUG_LOGGING) {
          console.log("[SCRUBBER] Fetch successful, getting blob");
        }
        const blob = await response.blob();
        if (USE_DEBUG_LOGGING) {
          console.log("[SCRUBBER] Blob size:", blob.size, "type:", blob.type);
        }

        if (blob.type === "text/html" || blob.type.includes("text")) {
          throw new Error(
            "Authentication failed - received HTML instead of XLSX file",
          );
        }

        if (blob.size < 100) {
          throw new Error(`Invalid file received. Size: ${blob.size} bytes`);
        }

        if (abortController.signal.aborted) return;

        const file = new File([blob], outputFileName, {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });

        if (USE_DEBUG_LOGGING) {
          console.log("[SCRUBBER] Starting jspreadsheet parser");
        }
        jspreadsheet.parser!({
          file,
          locale,
          onload: (config: any) => {
            if (abortController.signal.aborted) return;
            if (USE_DEBUG_LOGGING) {
              console.log(
                "[SCRUBBER] Parser onload - worksheets:",
                config.worksheets?.length,
                "style defs:",
                config.style?.length,
              );
            }

            if (config.worksheets && Array.isArray(config.worksheets)) {
              // Ensure unique worksheetName to avoid jspreadsheet clash warnings
              const nameCounts: Record<string, number> = {};
              config.worksheets.forEach((worksheet: any, idx: number) => {
                const baseName =
                  worksheet.worksheetName ||
                  worksheet.sheetName ||
                  `Sheet${idx + 1}`;
                nameCounts[baseName] = (nameCounts[baseName] || 0) + 1;
                worksheet.worksheetName =
                  nameCounts[baseName] > 1
                    ? `${baseName} (${nameCounts[baseName]})`
                    : baseName;

                worksheet.filters = enableFilters;
                worksheet.columnSorting = enableColumnSorting;
                worksheet.stripHTML = true;

                // Ensure enough empty rows exist so the grid fills the
                // viewport even when zoomed out (like Excel's infinite rows).
                const dataRows = worksheet.data?.length || 0;
                const minRows = Math.max(dataRows, 200);
                const minCols = worksheet.minDimensions?.[0] || 10;
                worksheet.minDimensions = [minCols, minRows];

                // Save and strip per-worksheet cell-to-style mappings.
                // jspreadsheet processes all mappings synchronously during
                // init which freezes the browser for 19k+ entries.  We
                // re-apply them after mount by writing directly to the
                // worksheet instance's style property and refreshing.
                if (worksheet.style) {
                  (worksheet as any)._deferredStyle = { ...worksheet.style };
                  delete worksheet.style;
                }

                // Apply read-only settings
                if (isReadOnly) {
                  worksheet.editable = false;
                  worksheet.allowInsertRow = false;
                  worksheet.allowInsertColumn = false;
                  worksheet.allowDeleteRow = false;
                  worksheet.allowDeleteColumn = false;
                  worksheet.allowRenameColumn = false;
                  worksheet.allowComments = false;
                }
              });
            }

            if (config.validations) {
              delete config.validations;
            }

            // Note: config.definedNames must be preserved for cross-sheet
            // formula references (e.g. Locations table) to resolve correctly.

            // Save global style definitions for deferred CSS injection,
            // then strip from config so jspreadsheet doesn't process them
            // during init (which causes a multi-second freeze).
            if (config.style) {
              (config as any)._styleDefs = config.style;
              delete config.style;
            }

            if (USE_DEBUG_LOGGING) {
              console.log(
                "[SCRUBBER] Setting worksheet data with",
                config.worksheets?.length,
                "worksheets",
              );
            }
            setWorksheetData(config);
            setIsLoading(false);
            loadingRef.current = false;
          },
          onerror: (error: any) => {
            if (abortController.signal.aborted) return;
            if (USE_DEBUG_LOGGING) {
              console.error("[SCRUBBER] Parser error:", error);
            }
            setIsLoading(false);
            loadingRef.current = false;
            if (onError) {
              onError(error);
            } else {
              alert(error);
            }
          },
        });
      } catch (error) {
        if (abortController.signal.aborted) return;
        if (USE_DEBUG_LOGGING) {
          console.error("[SCRUBBER] Load error:", error);
        }
        setIsLoading(false);
        loadingRef.current = false;
        if (onError) {
          onError(error);
        } else {
          alert(error);
        }
      }
    };

    loadSpreadsheet();

    return () => {
      abortController.abort();
      loadingRef.current = false;
    };
    // Note: authToken is intentionally excluded — we use authTokenRef so that
    // a token refresh doesn't trigger a full re-fetch and remount cycle, which
    // causes the blank screen bug (the @jspreadsheet/react wrapper's init guard
    // skips initialization when it sees a stale spreadsheetRef).
  }, [url, outputFileName, locale, enableFilters, enableColumnSorting]);

  if (isLoading) {
    if (USE_DEBUG_LOGGING) {
      console.log("[SCRUBBER] Rendering loading state");
    }
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
          width: "100%",
        }}
      >
        <div style={{ textAlign: "center" }}>
          <div
            style={{
              border: "4px solid #f3f3f3",
              borderTop: "4px solid #3498db",
              borderRadius: "50%",
              width: "40px",
              height: "40px",
              animation: "spin 1s linear infinite",
              margin: "0 auto 16px",
            }}
          />
          <div>Loading spreadsheet...</div>
        </div>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  if (!worksheetData || !worksheetData.worksheets) {
    if (USE_DEBUG_LOGGING) {
      console.log("[SCRUBBER] No worksheet data available");
    }
    return <div>No data loaded</div>;
  }

  if (USE_DEBUG_LOGGING) {
    console.log("[SCRUBBER] isReady:", isReady);
  }

  if (!isReady) {
    if (USE_DEBUG_LOGGING) {
      console.log("[SCRUBBER] Waiting for DOM to be ready");
    }
    return <div>Initializing...</div>;
  }

  if (USE_DEBUG_LOGGING) {
    console.log(
      "[SCRUBBER] Rendering spreadsheet with",
      worksheetData.worksheets.length,
      "worksheets",
    );
  }

  return (
    <div
      ref={containerRef}
      style={{
        height: "100%",
        overflow: "hidden",
        width: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Formula bar — preventDefault keeps focus in the spreadsheet */}
      <div className="jss-formula-bar" onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}>
        <div className="jss-formula-bar__cell">{formulaBarCell || "—"}</div>
        <div className="jss-formula-bar__separator" />
        <div className="jss-formula-bar__value">{formulaBarValue}</div>
      </div>
      {/* Wrapper constrains the spreadsheet so the status bar stays visible */}
      <div className="jss-spreadsheet-wrapper">
        <div
          className="jss-spreadsheet-scaler"
          style={{
            width: `${100 / zoomLevel}%`,
            height: `${100 / zoomLevel}%`,
            transform: `scale(${zoomLevel})`,
          }}
        >
          <Spreadsheet
            loading={false}
            ref={spreadsheetRef}
            tabs={!isReadOnly}
            toolbar={!isReadOnly}
            allowCreateWorksheet={!isReadOnly}
            allowDeleteWorksheet={!isReadOnly}
            allowRenameWorksheet={!isReadOnly}
            tableOverflow={true}
            tableWidth={"100%"}
            tableHeight={"100%"}
            fullscreen={true}
            onselection={handleSelection}
            onopenworksheet={handleOpenWorksheet}
          >
            {worksheetData.worksheets
              .map((worksheet: any, index: number) => (
                <Worksheet key={index} {...worksheet} />
              ))}
          </Spreadsheet>
        </div>
      </div>
      {/* Status bar — preventDefault+stopPropagation keeps focus in the spreadsheet */}
      <div
        className="jss-status-bar"
        onMouseDown={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
      >
        <div className="jss-status-bar__stats">
          {selectionStats && (
            <>
              <span>Count: {selectionStats.count}</span>
              {selectionStats.numericCount > 0 && (
                <>
                  <span>Sum: {formatStatNumber(selectionStats.sum)}</span>
                  <span>
                    Average:{" "}
                    {formatStatNumber(
                      selectionStats.sum / selectionStats.numericCount,
                    )}
                  </span>
                </>
              )}
            </>
          )}
        </div>
        <div className="jss-status-bar__zoom">
          <button
            className="jss-status-bar__zoom-btn"
            onClick={() => applyZoom(zoomLevel - ZOOM_STEP)}
            disabled={zoomLevel <= ZOOM_MIN}
            title="Zoom out"
          >
            &minus;
          </button>
          <span className="jss-status-bar__zoom-level">
            {Math.round(zoomLevel * 100)}%
          </span>
          <div className="jss-status-bar__zoom-dropdown">
            <button
              className="jss-status-bar__zoom-btn"
              onClick={() => setZoomMenuOpen((prev) => !prev)}
              title="Zoom presets"
            >
              &#9662;
            </button>
            {zoomMenuOpen && (
              <div className="jss-status-bar__zoom-menu">
                {ZOOM_PRESETS.map((pct) => (
                  <button
                    key={pct}
                    className="jss-status-bar__zoom-menu-item"
                    onClick={() => {
                      applyZoom(pct / 100);
                      setZoomMenuOpen(false);
                    }}
                  >
                    {pct}%
                  </button>
                ))}
              </div>
            )}
          </div>
          <button
            className="jss-status-bar__zoom-btn"
            onClick={() => applyZoom(zoomLevel + ZOOM_STEP)}
            disabled={zoomLevel >= ZOOM_MAX}
            title="Zoom in"
          >
            +
          </button>
        </div>
      </div>
    </div>
  );
};

export default PingSovScrubber;
